import { useEffect, useMemo, useState } from "react";
import api, { apiError } from "@/lib/api";
import { NUM, todayISO } from "@/lib/format";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { FileText, Download, Printer, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { buildStockReportPdf, summarize, SORTS, BASES } from "@/lib/stockReportPdf";

const TRY = (v) => `${new Intl.NumberFormat("tr-TR", { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(Number(v || 0))} ₺`;

function Choice({ map, value, onPick, prefix }) {
  return (
    <div className="grid grid-cols-2 gap-2">
      {Object.entries(map).map(([k, [lbl, desc]]) => (
        <button key={k} type="button" onClick={() => onPick(k)} data-testid={`${prefix}-${k}`}
          className={cn("rounded-xl border p-2.5 text-left transition-colors", value === k ? "border-primary bg-primary/5 ring-1 ring-primary" : "hover:bg-muted/50")}>
          <div className="font-bold text-[13px]">{lbl}</div><div className="text-[11px] text-muted-foreground leading-snug">{desc}</div>
        </button>
      ))}
    </div>
  );
}

/** Stok Maliyet Raporu (PDF) — özet + marka/ürün/genel sıralama, Depo/Mağaza miktarları, liste−iskonto veya son alış esası, %KDV. */
export default function StockReportDialog({ open, onOpenChange }) {
  const [sort, setSort] = useState("brand");
  const [basis, setBasis] = useState("list");
  const [kdv, setKdv] = useState(true);
  const [brand, setBrand] = useState("all");
  const [withOut, setWithOut] = useState(false);
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!open) return undefined;
    let live = true;   // eski (yavaş) yanıt yenisinin üstüne yazmasın
    setData(null);
    api.get(`/products/stock-report?basis=${basis}&kdv=true&scope=${withOut ? "all" : "instock"}`)
      .then((r) => { if (live) setData(r.data.items || []); })
      .catch((e) => { if (live) { toast.error(apiError(e)); setData([]); } });
    return () => { live = false; };
  }, [open, basis, withOut]);
  const brands = useMemo(() => [...new Set((data || []).map((r) => r.brand || "— Markasız —"))].sort((a, b) => a.localeCompare(b, "tr")), [data]);
  const rows = useMemo(() => (brand === "all" ? data || [] : (data || []).filter((r) => (r.brand || "— Markasız —") === brand)), [data, brand]);
  const S = useMemo(() => summarize(rows, kdv), [rows, kdv]);

  const make = async (print) => {
    if (!rows.length) return toast.error("Raporlanacak ürün yok");
    setBusy(true);
    try {
      const pdf = await buildStockReportPdf({ items: rows, sort, basis, kdv, withOut, brandFilter: brand === "all" ? "" : brand });
      if (print) { pdf.autoPrint(); window.open(pdf.output("bloburl"), "_blank"); }
      else pdf.save(`Stok Raporu ${todayISO()}${brand === "all" ? "" : ` ${brand}`}${withOut ? " (stoksuzlar dahil)" : ""}.pdf`);
    } catch (e) { toast.error("PDF oluşturulamadı: " + (e?.message || e)); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl max-h-[92vh] overflow-y-auto" data-testid="stock-report-dialog">
        <DialogHeader><DialogTitle className="font-head text-xl flex items-center gap-2"><FileText className="h-5 w-5 text-primary" />Stok Maliyet Raporu (PDF)</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div className="space-y-1.5"><div className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground">Sıralama</div><Choice map={SORTS} value={sort} onPick={setSort} prefix="rep-sort" /></div>
          <div className="space-y-1.5"><div className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground">Maliyet esası</div><Choice map={BASES} value={basis} onPick={setBasis} prefix="rep-basis" /></div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 items-end">
            <label className="flex items-center gap-2 rounded-xl border p-2.5 cursor-pointer" data-testid="rep-kdv"><Checkbox checked={kdv} onCheckedChange={(v) => setKdv(!!v)} /><span className="text-[13px] font-semibold">KDV dahil (%20) değerle</span></label>
            <Select value={brand} onValueChange={setBrand}>
              <SelectTrigger data-testid="rep-brand"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="all">Tüm markalar</SelectItem>{brands.map((b) => <SelectItem key={b} value={b}>{b}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <label className={cn("flex items-start gap-2 rounded-xl border p-2.5 cursor-pointer transition-colors", withOut && "border-primary bg-primary/5")} data-testid="rep-include-out">
            <Checkbox checked={withOut} onCheckedChange={(v) => setWithOut(!!v)} className="mt-0.5" data-testid="rep-include-out-check" />
            <span><span className="block text-[13px] font-semibold">Stokta olmayan ürünleri de göster</span><span className="block text-[11px] text-muted-foreground">Stoku 0 ve eksi olan ürünler de listelenir, PDF'te kırmızı "STOKTA YOK / EKSİ STOK" olarak işaretlenir; değerleri sıfır sayılır.</span></span>
          </label>
          <div className="rounded-xl bg-[#0F2745] text-white p-3 grid grid-cols-2 sm:grid-cols-4 gap-2" data-testid="rep-summary">
            {data === null ? <div className="col-span-full flex justify-center py-2"><Loader2 className="h-5 w-5 animate-spin" /></div> : [
              ["Ürün", NUM(S.count), "rep-sum-count"], ["Depo", NUM(S.depo), "rep-sum-depo"], ["Mağaza", NUM(S.mag), "rep-sum-mag"],
              [kdv ? "Değer (KDV'li)" : "Değer", TRY(kdv ? S.valueKdv : S.value), "rep-sum-value"],
            ].map(([l, v, t]) => <div key={l} className="min-w-0"><div className="text-[10px] uppercase tracking-wider text-slate-300 font-bold">{l}</div><div className="font-head font-black text-[15px] tabular-nums truncate text-[#F2B705]" data-testid={t}>{v}</div></div>)}
            {data !== null && withOut && <div className="col-span-full text-[11px] text-slate-300" data-testid="rep-sum-out">{NUM(S.count - S.zero - S.neg)} stokta · <span className="text-red-300 font-semibold">{NUM(S.zero)} stokta yok · {NUM(S.neg)} eksi stok</span></div>}
          </div>
        </div>
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={() => make(true)} disabled={busy || data === null} data-testid="rep-print"><Printer className="h-4 w-4 mr-2" />Yazdır</Button>
          <Button onClick={() => make(false)} disabled={busy || data === null} data-testid="rep-download">{busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Download className="h-4 w-4 mr-2" />}PDF İndir</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
